import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ChevronLeftIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getOl } from '../../api/ols/get-ol'
import { updateOl } from '../../api/ols/update-ol'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import OlActivityListPage from '../../components/pages/ols/activity/activity-list'
import OlCoachCompanyListPage from '../../components/pages/ols/coach-companies/coach-company-list'
import { HostFamilyTable } from '../../components/pages/ols/host-family/host-family-table'
import OlMobileNumberForFrenchLeadersListPage from '../../components/pages/ols/mobile-number-for-french-leaders/mobile-number-for-french-leaders-list'
import OlForm from '../../components/pages/ols/ol-form'
import OlHeader from '../../components/pages/ols/ol-header'
import OlHostFamilyPrices from '../../components/pages/ols/ol-host-family-prices'
import OlTeflTable from '../../components/pages/ols/ol-tefl/ol-tefl-table'
import OlTeflPrices from '../../components/pages/ols/ol-tefl-prices'
import OlTeachingHallsListPage from '../../components/pages/ols/teaching-halls/ol-teaching-halls'
import { mapOlToInput } from '../../components/pages/ols/utils'
import { Paths } from '../../lib/constants/path'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'
import { getOlName } from './center/utils/functions'
import OlHostFamilyPriceCreate from './views/ol-host-family-price-create'
import OlHostFamilyPriceUpdate from './views/ol-host-family-price-update'
import OlTeflPriceCreate from './views/ol-tefl-price-create'
import OlTeflPriceUpdate from './views/ol-tefl-price-update'

const OlGlobalRoutes: Record<string, string> = {
  hostFamilies: Paths.OL_HOST_FAMILY_GLOBAL_LIST_PAGE,
  tefls: Paths.OL_TEFL_GLOBAL_LIST_PAGE,
  teachingHalls: Paths.OL_TEACHING_HALL_GLOBAL_LIST_PAGE,
  coachCompanies: Paths.OL_COACH_COMPANIES_GLOBAL_LIST_PAGE
}

const OlUpdatePage = () => {
  const params = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()
  const location = useLocation()

  if (!params.id || isNaN(Number.parseInt(params.id)))
    return <ItemNotFound title="Identifiant de OL invalide" />

  const id = Number.parseInt(params.id)

  const { data: ol, isPending } = useQuery({
    queryKey: ['ol', id],
    queryFn: () => getOl(id)
  })

  const { mutateAsync: updateOlMutation } = useMutation({
    mutationFn: updateOl,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['ol', id] })
      toast.success('OL modifié avec succès')
    }
  })
  //check if the previous location is global or not
  const isFromGlobal =
    typeof location.state?.from === 'string' &&
    Object.values(OlGlobalRoutes).includes(location.state.from as string)

  if (isPending) return <LoadingScreen />
  if (!ol) return <NotFoundPage />
  return (
    <div>
      <UserNavBar
        title={getOlName(ol)}
        icon={ChevronLeftIcon}
        onIconClick={() =>
          isFromGlobal && typeof location.state?.from === 'string'
            ? back(location.state.from as string)
            : back(Paths.OL_LIST_PAGE)
        }
      />
      <div className={'h-4'} />

      <Box>
        <OlHeader data={ol} mode="update" />
        <div className={'h-4'} />

        <Routes>
          <Route
            path={Paths.OL_DETAILS_UPDATE_PAGE}
            element={
              <OlForm
                olId={id}
                defaultValues={mapOlToInput(ol)}
                buttonsConfig={updateButtonsConfig({
                  onCancel: () => back(),
                  onUpdate: (values) => updateOlMutation({ id, ...values }),
                  onUpdateThenCreate: async (values) => {
                    await updateOlMutation({ id, ...values })
                    navigate(Paths.OL_CREATE_PAGE)
                  },
                  onUpdateThenList: async (values) => {
                    await updateOlMutation({ id, ...values })
                    back()
                  }
                })}
              />
            }
          />
          <Route
            path={Paths.OL_DETAILS_HOST_FAMILIES_PAGE}
            element={<HostFamilyTable olId={id} mode="ol" />}
          />
          <Route
            path={Paths.OL_DETAILS_HOST_FAMILY_PRICES_PAGE}
            element={<OlHostFamilyPrices olId={id} />}
          />
          <Route
            path={Paths.OL_DETAILS_CREATE_HOST_FAMILY_PRICES_PAGE}
            element={<OlHostFamilyPriceCreate olId={id} />}
          />

          <Route
            path={Paths.OL_DETAILS_TEFLS_PAGE}
            element={<OlTeflTable olId={id} mode="ol" />}
          />

          <Route
            path={Paths.OL_DETAILS_UPDATE_HOST_FAMILY_PRICES_PAGE}
            element={<OlHostFamilyPriceUpdate olId={id} />}
          />
          <Route path={Paths.OL_DETAILS_TEFL_PRICES_PAGE} element={<OlTeflPrices olId={id} />} />
          <Route
            path={Paths.OL_DETAILS_CREATE_TEFL_PRICES_PAGE}
            element={<OlTeflPriceCreate olId={id} />}
          />
          <Route
            path={Paths.OL_DETAILS_TEACHING_HALLS_PAGE}
            element={<OlTeachingHallsListPage mode="ol" olId={id} />}
          />

          <Route
            path={Paths.OL_DETAILS_UPDATE_TEFL_PRICES_PAGE}
            element={<OlTeflPriceUpdate olId={id} />}
          />
          <Route
            path={Paths.OL_ACTIVITY_LIST_PAGE}
            element={<OlActivityListPage olId={id} currency={ol.currency} />}
          />
          <Route
            path={Paths.OL_MOBILE_NUMBERS_FOR_FRENCH_LEADERS_LIST_PAGE}
            element={<OlMobileNumberForFrenchLeadersListPage olId={id} />}
          />
          <Route
            path={Paths.OL_COACH_COMPANIES_LIST_PAGE}
            element={<OlCoachCompanyListPage olId={id} data={ol} mode="ol" />}
          />

          <Route path={'/'} element={<Navigate to={Paths.OL_DETAILS_UPDATE_PAGE} />} />
          <Route path={'*'} element={<NotFoundPage />} />
        </Routes>
      </Box>
    </div>
  )
}

export default OlUpdatePage
